import React, { useState } from 'react';
import { useGame } from '../state/gameStore.tsx';
import { api } from '../api/client.ts';
import { calculateDistance, calculateFleetSpeed, calculateMarchTimeSec, calculateFuelCost } from '@galaxy/shared';
import type { GalaxyEntity, FleetMissionType } from '@galaxy/shared';
import { He3Icon, PlanetIcon, PirateIcon, AsteroidIcon } from '../assets/icons.tsx';

interface DispatchFleetModalProps {
  target: GalaxyEntity;
  missionType: FleetMissionType;
  onClose: () => void;
  onSuccess: () => void;
}

export const DispatchFleetModal: React.FC<DispatchFleetModalProps> = ({ target, missionType, onClose, onSuccess }) => {
  const { state, refreshState } = useGame();
  const [selectedShips, setSelectedShips] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (!state) return null;

  const { planet, inventory, blueprints } = state;

  // Calculate distance
  const distance = calculateDistance(planet.coord_x, planet.coord_y, target.coord_x, target.coord_y);

  // Get selected hull types for speed
  const selectedHulls: string[] = [];
  let totalShipsSelected = 0;
  for (const [designId, count] of Object.entries(selectedShips)) {
    if (count > 0) {
      totalShipsSelected += count;
      const bp = blueprints.find(b => b.id === designId);
      if (bp) selectedHulls.push(bp.hull_id);
    }
  }

  const speed = calculateFleetSpeed(selectedHulls);
  const flightSec = calculateMarchTimeSec(distance, speed);
  const fuelCost = calculateFuelCost(distance, totalShipsSelected);

  const handleShipCountChange = (designId: string, count: number, maxCount: number) => {
    const validCount = Math.max(0, Math.min(maxCount, count));
    setSelectedShips(prev => ({ ...prev, [designId]: validCount }));
  };

  const handleDispatch = async () => {
    setErrorMsg(null);
    if (totalShipsSelected <= 0) {
      setErrorMsg('Vui lòng chọn ít nhất 1 tàu để điều động!');
      return;
    }

    if (planet.resources.he3 < fuelCost) {
      setErrorMsg(`Không đủ nhiên liệu Heli-3 (Cần: ${fuelCost}, Hiện có: ${Math.floor(planet.resources.he3)})!`);
      return;
    }

    try {
      setLoading(true);
      const shipsPayload = Object.entries(selectedShips)
        .filter(([_, count]) => count > 0)
        .map(([designId, count]) => ({ design_id: designId, count }));

      await api.dispatchFleet({
        target_type: target.entity_type === 'player_planet' ? 'planet' : 'galaxy_entity',
        target_id: target.id,
        target_x: target.coord_x,
        target_y: target.coord_y,
        mission_type: missionType,
        ships: shipsPayload,
      });

      await refreshState();
      onSuccess();
    } catch (err: any) {
      setErrorMsg(err.message || 'Lỗi khi điều động hạm đội!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 5, 15, 0.85)',
      backdropFilter: 'blur(6px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '16px',
    }}>
      <div style={{
        background: '#091528',
        border: '1px solid #00f0ff',
        borderRadius: 8,
        width: '100%',
        maxWidth: '420px',
        maxHeight: '90vh',
        overflowY: 'auto',
        padding: '16px',
        boxShadow: '0 0 24px rgba(0, 240, 255, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(0, 240, 255, 0.2)', paddingBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: '18px' }}>🚀</span>
            <span style={{ fontWeight: 'bold', color: '#00f0ff', fontSize: '15px' }}>
              ĐIỀU QUÂN: {missionType === 'attack' ? 'TẤN CÔNG' : missionType === 'scout' ? 'TRINH SÁT' : 'THU GOM'}
            </span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#88a0c0', fontSize: '18px', cursor: 'pointer' }}>
            ✕
          </button>
        </div>

        {/* Target Info */}
        <div style={{ background: 'rgba(0,0,0,0.4)', padding: '10px', borderRadius: 6, fontSize: '12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#88a0c0' }}>Mục tiêu:</span>
            <strong style={{ color: '#fff' }}>{target.name}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#88a0c0' }}>Tọa độ đích:</span>
            <strong style={{ color: '#00f0ff' }}>S{target.sector_id}:[{target.coord_x}, {target.coord_y}]</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#88a0c0' }}>Khoảng cách:</span>
            <strong style={{ color: '#ffd700' }}>{distance} AU</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#88a0c0' }}>Thời gian bay ước tính:</span>
            <strong style={{ color: '#00e5ff' }}>{flightSec} giây</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#88a0c0' }}>Tiêu hao nhiên liệu:</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#00e5ff', fontWeight: 'bold' }}>
              <He3Icon size={14} /> {fuelCost} He-3
            </span>
          </div>
        </div>

        {/* Ship Selection from Inventory */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#a0c0e0' }}>
            CHỌN TÀU CHIẾN TRONG KHO CẢNG:
          </div>

          {blueprints.length === 0 ? (
            <div style={{ color: '#ff6666', fontSize: '12px' }}>Chưa có bản thiết kế tàu chiến nào!</div>
          ) : (
            blueprints.map(bp => {
              const ownedCount = inventory[bp.id] || 0;
              const selectedCount = selectedShips[bp.id] || 0;

              return (
                <div key={bp.id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'rgba(10, 20, 35, 0.6)',
                  border: '1px solid rgba(0, 240, 255, 0.2)',
                  borderRadius: 6,
                  padding: '8px 10px',
                }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#fff' }}>{bp.name}</div>
                    <div style={{ fontSize: '11px', color: '#88a0c0' }}>
                      Có sẵn: <strong style={{ color: '#00f0ff' }}>{ownedCount}</strong> | Tốc độ: {bp.stats.speed}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button
                      onClick={() => handleShipCountChange(bp.id, selectedCount - 1, ownedCount)}
                      style={{ background: '#003355', border: '1px solid #00f0ff', color: '#fff', width: 24, height: 24, borderRadius: 4, cursor: 'pointer' }}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      value={selectedCount}
                      onChange={(e) => handleShipCountChange(bp.id, parseInt(e.target.value) || 0, ownedCount)}
                      style={{ width: 40, textAlign: 'center', background: '#050c18', border: '1px solid #335577', color: '#fff', borderRadius: 4, padding: '2px 0', fontSize: '12px' }}
                    />
                    <button
                      onClick={() => handleShipCountChange(bp.id, selectedCount + 1, ownedCount)}
                      style={{ background: '#003355', border: '1px solid #00f0ff', color: '#fff', width: 24, height: 24, borderRadius: 4, cursor: 'pointer' }}
                    >
                      +
                    </button>
                    <button
                      onClick={() => handleShipCountChange(bp.id, ownedCount, ownedCount)}
                      style={{ background: 'rgba(255, 215, 0, 0.2)', border: '1px solid #ffd700', color: '#ffd700', fontSize: '10px', borderRadius: 4, padding: '3px 6px', cursor: 'pointer' }}
                    >
                      Tất Cả
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {errorMsg && (
          <div style={{ color: '#ff4444', fontSize: '12px', background: 'rgba(255,0,0,0.1)', padding: '6px 8px', borderRadius: 4 }}>
            ⚠️ {errorMsg}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button
            onClick={onClose}
            style={{ flex: 1, background: '#223344', border: 'none', color: '#a0b0c0', padding: '10px', borderRadius: 4, fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}
          >
            HỦY BỎ
          </button>
          <button
            onClick={handleDispatch}
            disabled={loading || totalShipsSelected === 0}
            style={{
              flex: 2,
              background: totalShipsSelected > 0 ? '#ff0055' : '#552233',
              border: 'none',
              color: '#fff',
              padding: '10px',
              borderRadius: 4,
              fontSize: '13px',
              fontWeight: 'bold',
              cursor: totalShipsSelected > 0 ? 'pointer' : 'not-allowed',
            }}
          >
            {loading ? 'ĐANG ĐIỀU ĐỘNG...' : `XUẤT KÍCH (${totalShipsSelected} TÀU)`}
          </button>
        </div>
      </div>
    </div>
  );
};
